/**
 * Remembers each history entry's search params, keyed by the entry's
 * position in the session history -- so code about to go *back* can know
 * what it will land on before it pops.
 *
 * The History API has no "read the previous entry" primitive, only
 * imperative `back()`/`go()`. But `@tanstack/history` stamps every entry's
 * `state` with its own index (`__TSR_index`, incremented on push, kept on
 * replace), so recording `index -> search` each time the router resolves a
 * location is enough to answer "what's at `currentIndex - 1`?". Replaces
 * update their own slot in place, which is what makes this correct for
 * `basic`'s home: opening a drawer `replace`s `?dialog=...` onto the very
 * entry Back later returns to.
 *
 * Mirrored to `sessionStorage` (per-tab, same lifetime as the history stack
 * it describes) so a reload in the middle of the stack -- e.g. refreshing
 * the reader -- still knows what's behind it. Best-effort throughout: a
 * missing entry just means "unknown", which callers treat as "no dialog".
 */
const STORAGE_KEY = 'cf-history-entry-search';

type SearchRecord = Record<string, unknown>;

let entries: Record<number, SearchRecord> = readStored();

function readStored(): Record<number, SearchRecord> {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		return raw ? (JSON.parse(raw) as Record<number, SearchRecord>) : {};
	} catch {
		return {};
	}
}

function persist(): void {
	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
	} catch {
		// Storage full/blocked: the in-memory copy still covers this session.
	}
}

interface TrackedLocation {
	state: unknown;
	search: unknown;
}

export function historyEntryIndex(location: TrackedLocation): number | null {
	const index = (location.state as { __TSR_index?: unknown } | null)
		?.__TSR_index;
	return typeof index === 'number' ? index : null;
}

export function recordHistoryEntry(location: TrackedLocation): void {
	const index = historyEntryIndex(location);
	if (index === null) {
		return;
	}
	entries = {
		...entries,
		[index]: (location.search ?? {}) as SearchRecord,
	};
	persist();
}

/** The recorded search params of the entry `history.back()` would land on. */
export function getPreviousHistoryEntrySearch(
	location: TrackedLocation,
): SearchRecord | undefined {
	const index = historyEntryIndex(location);
	if (index === null || index < 1) {
		return undefined;
	}
	return entries[index - 1];
}

/** Test-only: forget everything recorded so far. */
export function resetHistoryEntryTracker(): void {
	entries = {};
	try {
		sessionStorage.removeItem(STORAGE_KEY);
	} catch {
		// Ignore -- see `persist`.
	}
}
